import CountdownTimer from "./CountdownTimer";
import { Network, Server, HardDrive, Database, Layers, Settings, Cloud, Shield, Container, AppWindow, Terminal } from "lucide-react";

const modules = [
  { module: 1, title: "Networking", description: "VCN, Subnets, Security Lists, Route Tables, Gateways, Load Balancers, DNS, and Network Peering in OCI.", icon: Network },
  { module: 2, title: "Compute", description: "VM & BM instances, Custom Images, Instance Pools, Autoscaling, and Dedicated Hosts on OCI.", icon: Server },
  { module: 3, title: "Storage", description: "Block Volume, File Storage, Object Storage, Archive Storage, and Storage Gateway.", icon: HardDrive },
  { module: 4, title: "OCI Database Service", description: "Autonomous DB, DB Systems, Exadata, MySQL, NoSQL Database, and Database Migration to OCI.", icon: Database },
  { module: 5, title: "OCI Resource Manager for Terraform", description: "Infrastructure as Code, Stacks, Jobs, Drift Detection, and automated provisioning with Terraform on OCI.", icon: Layers },
  { module: 6, title: "OCI Ansible", description: "Ansible Collections for OCI, Playbooks, Dynamic Inventory, Configuration Management, and automation.", icon: Settings },
  { module: 7, title: "Multicloud", description: "OCI-Azure Interconnect, Multi-cloud architecture patterns, Oracle Database@Azure, and hybrid strategies.", icon: Cloud },
  { module: 8, title: "Identity & Access Management", description: "OCI IAM policies, compartments, users, groups, dynamic groups, federation, and authentication mechanisms.", icon: Shield },
  { module: 9, title: "Kubernetes & Docker on OCI", description: "OKE (Oracle Kubernetes Engine), container instances, Docker registry, cluster management, and orchestration.", icon: Container },
  { module: 10, title: "Oracle Enterprise / Database Migration", description: "Zero-downtime migration (ZDM), Database Migration, Oracle GoldenGate, SQL Developer, Data Pump.", icon: AppWindow },
];

export default function OCICourseSection() {
  return (
    <section id="oci-course" data-testid="oci-course-section" className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <Terminal size={24} className="text-[#E32636]" />
        <p className="font-mono text-xs text-[#E32636] uppercase tracking-widest">// course 02</p>
      </div>
      <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-[#F4F4F5] mb-3">
        Complete OCI Mastery <span className="text-[#E32636]">for Oracle DBA</span>
      </h2>
      <p className="text-base text-[#A1A1AA] max-w-2xl mb-10">
        Oracle DBA &rarr; OCI Architect / Cloud DBA &rarr; AIML Data Architect. 10 comprehensive modules covering the full Oracle Cloud Infrastructure stack.
      </p>

      {/* Details + Countdown row */}
      <div className="grid lg:grid-cols-2 gap-6 mb-12">
        <div className="p-6 bg-[#16161F] border border-[#27272A] rounded-md glow-cyan">
          <h3 className="font-['Space_Grotesk'] text-sm font-semibold text-[#22D3EE] uppercase tracking-wider mb-4">Course Details</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-[#A1A1AA]">
              <span className="text-[#71717A]">Duration</span>
              <span>60 to 65 Hours</span>
            </div>
            <div className="flex justify-between text-[#A1A1AA]">
              <span className="text-[#71717A]">Schedule</span>
              <span>Monday to Thursday</span>
            </div>
            <div className="flex justify-between text-[#A1A1AA]">
              <span className="text-[#71717A]">Free Demo</span>
              <span className="text-[#E32636] font-semibold">30-May-2026</span>
            </div>
            <div className="flex justify-between text-[#A1A1AA]">
              <span className="text-[#71717A]">Start Date</span>
              <span>03-June-2026</span>
            </div>
            <div className="flex justify-between text-[#A1A1AA]">
              <span className="text-[#71717A]">Time</span>
              <span>08:30 AM - 10:00 AM IST</span>
            </div>
            <div className="flex justify-between text-[#A1A1AA]">
              <span className="text-[#71717A]">Modules</span>
              <span>10 Modules</span>
            </div>
            <div className="flex justify-between text-[#A1A1AA]">
              <span className="text-[#71717A]">Contact</span>
              <span className="text-[#22D3EE]">+91-8978266997</span>
            </div>
          </div>
        </div>

        <CountdownTimer targetDate="2026-06-05T08:30:00+05:30" label="OCI Course Enrollment Closes In" accentColor="#E32636" />
      </div>

      {/* Syllabus bento grid */}
      <h3 className="font-['Space_Grotesk'] text-lg font-semibold text-[#F4F4F5] mb-4">Syllabus</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#27272A] border border-[#27272A] rounded-md overflow-hidden">
        {modules.map((mod) => {
          const Icon = mod.icon;
          return (
            <div
              key={mod.module}
              data-testid={`syllabus-module-${mod.module}`}
              className="p-6 bg-[#111118] hover:bg-[#16161F] transition-all duration-300 group cursor-default"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-md bg-[#0A0A0F] border border-[#27272A] group-hover:border-[#22D3EE]/50 group-hover:shadow-[0_0_10px_rgba(34,211,238,0.1)] transition-all">
                  <Icon size={18} className="text-[#71717A] group-hover:text-[#22D3EE] transition-colors" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-mono text-[#22D3EE] uppercase tracking-wider">M{String(mod.module).padStart(2, "0")}</span>
                  </div>
                  <h4 className="font-['Space_Grotesk'] text-base font-semibold text-[#F4F4F5] mb-1 leading-tight">
                    {mod.title}
                  </h4>
                  <p className="text-xs text-[#71717A] leading-relaxed">
                    {mod.description}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
